import { useEffect, useState, type ReactNode } from 'react';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { promptsService } from '@/services/prompts.service';
import { learnService } from '@/services/learn.service';
import { ApiError } from '@/services/http';
import { InlineLoader } from '@/components/ui/InlineLoader';
import { cn } from '@/lib/cn';
import type { PromptView } from '@/types/api';

/**
 * Per-word stagger between cascade animations. 14ms feels alive without
 * dragging — at this rate a 200-word answer settles in ~3.1s, which is
 * within the time a reader would naturally take to look down at the
 * card. Bumping this up makes long answers feel sluggish; bumping it
 * down erases the cascade and the response just flashes in.
 */
const CASCADE_WORD_DELAY_MS = 14;

/**
 * LearnWithOlliePanel — the "Learn With Ollie" section inside
 * `BookDetailModal` per docs/LIBRARY.md Artboard 2.
 *
 * Flow:
 *   1. On mount, list the curated prompts admins have attached to the
 *      book. Free-form questions are intentionally NOT supported here —
 *      readers (often kids) only see admin-vetted questions.
 *   2. Clicking a pill with a cached response shows it instantly.
 *      Clicking a pill *without* a cached response fires `POST /learn`
 *      with the stored `promptId`, which caches the answer server-side
 *      so subsequent readers don't re-bill OpenAI for the same question.
 *
 * The panel intentionally keeps only one active response in view at a
 * time — this matches the mockups and keeps the modal height bounded.
 */
export interface LearnWithOlliePanelProps {
  bookId: string;
  className?: string;
}

export function LearnWithOlliePanel({ bookId, className }: LearnWithOlliePanelProps) {
  const [prompts, setPrompts] = useState<PromptView[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(true);

  const [activePromptId, setActivePromptId] = useState<string | null>(null);
  /**
   * The question text shown above the response card. Mirrors the
   * clicked prompt's text so we don't have to re-derive it from
   * `activePromptId` during render.
   */
  const [activeQuestion, setActiveQuestion] = useState<string | null>(null);
  const [activeResponse, setActiveResponse] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);

  // --- Load prompts for the book whenever it changes. ---
  useEffect(() => {
    let cancelled = false;
    setPromptsLoading(true);
    setPrompts([]);
    setActivePromptId(null);
    setActiveQuestion(null);
    setActiveResponse(null);

    (async () => {
      try {
        const rows = await promptsService.listForBook(bookId);
        if (cancelled) return;
        // Server returns newest-first. The UI reads better with oldest
        // suggestions on the left so admins can curate "start here" as
        // the first prompt — flip to ascending.
        setPrompts(rows.slice().reverse());
      } catch (err) {
        if (cancelled) return;
        // The Learn panel is an auxiliary surface — a failed prompt list
        // shouldn't cause a toast storm. Log and render the empty state.
        console.warn('Prompt list failed:', err);
        setPrompts([]);
      } finally {
        if (!cancelled) setPromptsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bookId]);

  const askStoredPrompt = async (row: PromptView) => {
    if (asking) return;
    const questionText = row.prompt?.trim();
    if (!questionText) return;

    setActivePromptId(row.id);
    setActiveQuestion(questionText);

    // If the server already has a cached response, render it instantly
    // — no need to burn an OpenAI call.
    if (row.response) {
      setActiveResponse(row.response);
      return;
    }

    setAsking(true);
    setActiveResponse(null);
    try {
      const result = await learnService.ask({
        bookId,
        prompt: questionText,
        promptId: row.id,
      });
      setActiveResponse(result.response);
      // Patch the local prompt list so a second click doesn't re-ask.
      setPrompts((prev) =>
        prev.map((p) =>
          p.id === row.id
            ? { ...p, response: result.response, responseCreatedAt: new Date().toISOString() }
            : p,
        ),
      );
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Ollie could not answer just now.';
      toast.error(message);
      setActiveResponse(null);
    } finally {
      setAsking(false);
    }
  };

  // The whole panel is admin-curated — when a book has no prompts the
  // empty state below is the entire UI. There's intentionally no
  // free-form fallback here; ad-hoc asks would bypass the admin curation
  // that keeps Learn With Ollie kid-safe.
  return (
    <section
      className={cn(
        'rounded-2xl border border-or-orange-100 bg-gradient-to-br from-or-orange-50/60 via-white to-or-cream-50/60 p-5',
        className,
      )}
      aria-labelledby="learn-with-ollie-heading"
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-or-orange-100 text-or-orange-700">
          <Sparkles className="h-4 w-4" aria-hidden />
        </span>
        <div>
          <div className="or-kicker">Ask Ollie</div>
          <h3
            id="learn-with-ollie-heading"
            className="font-display text-[16px] font-bold text-or-slate-900"
          >
            Ask a question about this book
          </h3>
        </div>
      </div>

      {/* Suggested prompt pills */}
      <div className="mt-4 flex flex-wrap gap-2">
        {promptsLoading ? (
          <InlineLoader label="Loading suggested questions…" />
        ) : prompts.length === 0 ? (
          <div className="text-[13px] text-or-slate-500">
            No questions yet for this book — check back soon.
          </div>
        ) : (
          prompts
            .filter((p) => p.prompt && p.prompt.trim().length > 0)
            .map((row) => {
              const isActive = row.id === activePromptId;
              return (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => askStoredPrompt(row)}
                  disabled={asking}
                  aria-pressed={isActive}
                  className={cn(
                    'rounded-full border px-3.5 py-1.5 text-left text-[12px] font-semibold transition',
                    isActive
                      ? 'border-or-orange-300 bg-or-orange-100 text-or-orange-800'
                      : 'border-or-orange-200 bg-white text-or-orange-700 hover:bg-or-orange-50',
                    'disabled:cursor-not-allowed disabled:opacity-60',
                  )}
                >
                  {row.prompt}
                </button>
              );
            })
        )}
      </div>

      {/* Response card — mounts only when there's something to show. */}
      {activeQuestion ? (
        <div className="mt-4 rounded-xl border border-or-slate-100 bg-white p-4 shadow-or-card">
          <div className="or-kicker">Question</div>
          <p className="mt-1 text-[14px] font-semibold text-or-slate-900">{activeQuestion}</p>
          <div className="or-kicker mt-3">Ollie says</div>
          {/* aria-live on the wrapper so screen readers announce when
              the skeleton is replaced by the cascading response text. */}
          <div aria-live="polite" aria-busy={asking}>
            {asking ? (
              <div className="mt-2 space-y-2">
                <div className="h-3 w-11/12 animate-pulse rounded bg-or-slate-200" />
                <div className="h-3 w-10/12 animate-pulse rounded bg-or-slate-200" />
                <div className="h-3 w-9/12 animate-pulse rounded bg-or-slate-200" />
              </div>
            ) : activeResponse ? (
              // `key` bound to the response text forces a remount whenever
              // a different prompt is clicked or a fresh answer arrives —
              // without that, React would just patch the inner text of a
              // surviving <p> and the staggered animation would never
              // re-trigger on the second click.
              <CascadeText key={activeResponse} text={activeResponse} />
            ) : (
              <p className="mt-1 text-[14px] text-or-slate-500">
                Something went wrong fetching an answer. Try again in a moment.
              </p>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Renders a block of prose with each word fading in and sliding down on
 * a small staggered delay. Whitespace between words is rendered as raw
 * text nodes (rather than wrapped in spans) so the browser's native
 * line-breaking treats them as normal collapsible space — without this,
 * `inline-block` word spans would lose their wrap points on narrow
 * widths.
 *
 * The component is intentionally pure: the keyframe + base styles live
 * in `or-cascade-word` (see styles/index.css), and per-word delays are
 * passed inline. That keeps the React side trivial and lets the browser
 * own the animation timing rather than running a JS loop.
 */
function CascadeText({ text }: { text: string }) {
  // Split with a capturing group so whitespace tokens are preserved in
  // the output array — letting us re-emit them between word spans.
  const tokens = text.split(/(\s+)/);
  let wordIndex = 0;
  const children: ReactNode[] = [];

  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (!token) continue;
    if (/^\s+$/.test(token)) {
      // Raw text node: native whitespace handling, supports wrapping.
      children.push(token);
      continue;
    }
    children.push(
      <span
        key={i}
        className="or-cascade-word"
        style={{ animationDelay: `${wordIndex * CASCADE_WORD_DELAY_MS}ms` }}
      >
        {token}
      </span>,
    );
    wordIndex += 1;
  }

  return (
    <p className="mt-1 whitespace-pre-wrap text-[14px] leading-relaxed text-or-slate-700">
      {children}
    </p>
  );
}
